import { CustomerWrapper } from './booking-customer.style'
import { Button, message, Select, theme } from 'antd'
import { getCustomerName, getShopId, queryClient, TranslateFunction, useTranslate } from 'utils'
import { useEffect, useRef, useState } from 'react'
import { Avatar } from 'components/avatar/avatar'
import { Loading, NotFound, PageError } from 'components/styled'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { DEBOUNCING_TIMEOUT } from 'app.constants'
import { FormSection } from 'components/form-section/form-section'
import { BookingAction, BookingPageDispatch, BookingPageState } from 'pages/booking/booking-page/booking-page'
import { Customer, Feature } from 'types'
import { useConfig, useCreateCustomer, useCustomers } from 'api'
import { BookingAddCustomer } from '../booking-add-customer/booking-add-customer'
import { getCustomerDefaultValues } from 'pages/customer/customer-form/customer-form'

type Props = {
    customerPreset: Customer | null
    pageState: BookingPageState
    pageStateDispatch: BookingPageDispatch
}

export const BookingCustomer = ({ customerPreset, pageState, pageStateDispatch }: Props) => {

    //hooks:
    const { __ } = useTranslate()
    const { token: { fontSizeSM, marginXS } } = theme.useToken()
    const { data: config } = useConfig()

    const hasCustomerFeature = config && config.features.includes(Feature.CUSTOMER_MANAGEMENT)

    //debounce timeout:
    const lastKeyPressTimeoutId = useRef(0 as number | NodeJS.Timeout)

    //state:
    const [ search, setSearch ] = useState<string | null>(null)
    const [ isTyping, setIsTyping ] = useState(false)
    const [ savingValues , setSavingValues  ] = useState(getCustomerDefaultValues())

    //api:
    const { data: customers, isLoading: isCustomersLoading, isFetching: isCustomerFetching } = useCustomers(search, 10)
    const { mutate: createCustomer, isLoading: isCreateLoading, data: createResponse } = useCreateCustomer(savingValues)

    //create
    useEffect(() => {
        if(savingValues.id){
            createCustomer()
        }
    }, [savingValues])

    //after create:
    useEffect(() => {
        if(createResponse){
            if(createResponse === 'customer_exists'){
                message.error(__`oops_something_went_wrong`)
            }else{
                queryClient.invalidateQueries(['customers', getShopId()])
                pageStateDispatch({ type: BookingAction.SET_CUSTOMER, payload: { customer: createResponse } })
            }
        }
    }, [isCreateLoading])

    //searchCustomers:
    const searchCustomers = (searchPhrase: string) => {
        setIsTyping(true)
        clearTimeout(lastKeyPressTimeoutId.current)
        if(searchPhrase.length < 1){
            setSearch(null)
            setIsTyping(false)
        }else{
            lastKeyPressTimeoutId.current = setTimeout(() => {
                setSearch(searchPhrase)
                setIsTyping(false)
            }, DEBOUNCING_TIMEOUT)
        }
    }
    
    // Set page state customer
    useEffect(() => {
        if(customerPreset && !pageState.customer){
            pageStateDispatch({ type: BookingAction.SET_CUSTOMER, payload: { customer: customerPreset } })
        }
    }, [customerPreset])

    return (
        <FormSection
            title={__`customer`}
            style={{ gap: marginXS }}
            topButton={(customerPreset || !hasCustomerFeature) ? undefined : {
                label: __`add_new_customer`,
                icon: <PlusOutlined/>,
                disabled: isCreateLoading,
                onClick: () => pageStateDispatch({ type: BookingAction.SET_IS_ADD_CUSTOMER_OPEN, payload: { isAddCustomerOpen: true } })
            }}
        >

            {(pageState.customer || customerPreset) ? (
                
                <CustomerWrapper>

                    {pageState.customer && (
                        <>

                            {/* selected customer */}
                            <Avatar
                                name={getCustomerName(pageState.customer) ?? __`unknown`}
                                description={pageState.customer.email ?? pageState.customer.phone}
                                image={pageState.customer.image.path}
                                style={{ marginLeft: -marginXS }}
                            />

                            {/* clear button */}
                            {!customerPreset && (
                                <Button
                                    type='text'
                                    size='large'
                                    shape='circle'
                                    onClick={() => pageStateDispatch({ type: BookingAction.SET_CUSTOMER, payload: { customer: null } })}
                                >
                                    <CloseOutlined style={{ fontSize: fontSizeSM }}/>
                                </Button>
                            )}

                        </>
                    )}

                </CustomerWrapper>

            ) : isCreateLoading ? <Loading/> : (

                <Select
                    placeholder={__`search_name_email_phone`}
                    showSearch
                    allowClear
                    disabled={isCreateLoading}
                    onSearch={searchCustomers}
                    onSelect={value => {
                        if(customers){
                            const customer = getCustomerFromValue(customers, value)
                            if(customer && customer.id === customer.externalId && hasCustomerFeature){ // It is not from the DB
                                setSavingValues(customer)
                            }else{
                                pageStateDispatch({ type: BookingAction.SET_CUSTOMER, payload: { customer } })
                            }
                        }
                    }}
                    notFoundContent={
                        (isCustomersLoading || isCustomerFetching || isTyping) ? <Loading/> :
                        !customers ? <PageError message={__`error`}/> :
                        <NotFound message={search ? undefined : __`no_customers`}/>
                    }
                    style={{ width: '100%' }}
                >
                    {(isCustomersLoading || isCustomerFetching || isTyping) ? <Loading/> : customers && getOptions(customers, __)}
                </Select>
            
            )}

            <BookingAddCustomer
                pageState={pageState}
                pageStateDispatch={pageStateDispatch}
            />
            
        </FormSection>
    )

}

const getOptions = (customers: Customer[], __: TranslateFunction) => customers.map(customer =>
    <Select.Option key={`${customer.id} ${customer.firstName} ${customer.lastName} ${customer.email} ${customer.phone}`}>
        <Avatar
            size='small'
            name={getCustomerName(customer) ?? __`unknown`}
            description={customer.email ?? customer.phone}
            image={customer.image.path}
        />
    </Select.Option>
)

const getCustomerFromValue = (customers: Customer[], value: string) => {
    const splitValue = value.split(' ')
    const id = splitValue[0]
    return customers.find(customer => customer.id === id) ?? null
}
